import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSessionStore } from 'src/stores/useSessionStore';
import {
  captureSessionException,
  captureSessionMessage,
} from 'src/utils/sentry/sessionSentry';

/**
 * Session expiration configuration constants
 */
export const SESSION_EXPIRATION_CONFIG = {
  /** Warning threshold in seconds before session expires */
  WARNING_THRESHOLD_SECONDS: 5 * 60, // 5 minutes
  /** How often to check session expiration (ms) */
  CHECK_INTERVAL_MS: 1000,
  /** Development mode uses shorter intervals for testing */
  DEV_WARNING_THRESHOLD_SECONDS: 60, // 1 minute in dev
} as const;

export interface SessionExpirationState {
  /** Whether the session is expiring soon (within warning threshold) */
  isExpiringSoon: boolean;
  /** Seconds remaining until session expires */
  remainingSeconds: number;
  /** ISO timestamp of when session expires */
  expiresAt: string | null;
  /** Whether the current session is valid/active */
  isSessionActive: boolean;
  /** Whether session data has been loaded */
  isLoaded: boolean;
}

export interface UseSessionExpirationReturn extends SessionExpirationState {
  /** Refresh session by making an API call (updates lastActivity) */
  refreshSession: () => Promise<void>;
  /** Force fetch current session data */
  fetchCurrentSession: () => Promise<void>;
}

/**
 * Hook to monitor current session expiration
 * Provides countdown and warning state for session-based (not token-based) expiration
 */
export function useSessionExpiration(): UseSessionExpirationReturn {
  const { currentSession, actions } = useSessionStore();
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [isLoaded, setIsLoaded] = useState(false);
  const previousExpirationStateRef = useRef<{
    isExpiringSoon: boolean;
    isSessionActive: boolean;
    remainingSeconds: number;
    expiresAt: string | null;
  } | null>(null);

  // Get warning threshold based on environment
  const warningThreshold =
    process.env.NODE_ENV === 'development'
      ? SESSION_EXPIRATION_CONFIG.DEV_WARNING_THRESHOLD_SECONDS
      : SESSION_EXPIRATION_CONFIG.WARNING_THRESHOLD_SECONDS;

  // Calculate session expiration state
  // Note: currentTime triggers recalculation every second for countdown
  const expirationState = useMemo((): SessionExpirationState => {
    const expiresAt = currentSession?.expiresAt ?? null;
    // Calculate remaining seconds based on current time
    const now = currentTime; // Use currentTime to ensure recalculation on each tick
    const expirationTime = expiresAt ? new Date(expiresAt).getTime() : 0;
    const remainingMs = expirationTime - now;
    const remainingSeconds = Math.max(0, Math.floor(remainingMs / 1000));
    const isExpiringSoon =
      remainingSeconds > 0 && remainingSeconds <= warningThreshold;
    const isSessionActive = currentSession?.isActive ?? false;

    return {
      isExpiringSoon,
      remainingSeconds,
      expiresAt,
      isSessionActive,
      isLoaded,
    };
  }, [currentSession, warningThreshold, isLoaded, currentTime]);

  // Update current time periodically for countdown
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, SESSION_EXPIRATION_CONFIG.CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, []);

  // Fetch current session on mount
  const fetchCurrentSession = useCallback(async () => {
    captureSessionMessage('Fetching current session state', 'info', {
      event: 'session.current.fetch.requested',
    });

    try {
      await actions.fetchSessions();
      setIsLoaded(true);
      captureSessionMessage('Current session state loaded', 'info', {
        event: 'session.current.fetch.succeeded',
      });
    } catch (error) {
      console.error('Failed to fetch session:', error);
      setIsLoaded(true);
      captureSessionException(error, {
        event: 'session.fetchCurrent.failed',
      });
    }
  }, [actions]);

  // Initial fetch
  useEffect(() => {
    if (!isLoaded && !currentSession) {
      fetchCurrentSession();
    } else if (currentSession) {
      setIsLoaded(true);
    }
  }, [isLoaded, currentSession, fetchCurrentSession]);

  useEffect(() => {
    if (!isLoaded) return;

    const previous = previousExpirationStateRef.current;
    const current = {
      isExpiringSoon: expirationState.isExpiringSoon,
      isSessionActive: expirationState.isSessionActive,
      remainingSeconds: expirationState.remainingSeconds,
      expiresAt: expirationState.expiresAt,
    };

    if (!previous) {
      captureSessionMessage(
        'Initial session expiration state captured',
        'info',
        {
          event: 'session.expiration.state.initialized',
          extra: {
            expiresAt: current.expiresAt,
            remainingSeconds: current.remainingSeconds,
            isExpiringSoon: current.isExpiringSoon,
            isSessionActive: current.isSessionActive,
            warningThresholdSeconds: warningThreshold,
          },
        },
      );
      previousExpirationStateRef.current = current;
      return;
    }

    if (
      previous.expiresAt !== current.expiresAt ||
      previous.isSessionActive !== current.isSessionActive
    ) {
      captureSessionMessage('Session metadata changed', 'info', {
        event: 'session.expiration.state.changed',
        extra: {
          previousExpiresAt: previous.expiresAt,
          expiresAt: current.expiresAt,
          previousIsSessionActive: previous.isSessionActive,
          isSessionActive: current.isSessionActive,
          remainingSeconds: current.remainingSeconds,
        },
      });
    }

    if (!previous.isExpiringSoon && current.isExpiringSoon) {
      captureSessionMessage('Session entered expiring-soon window', 'warning', {
        event: 'session.expiration.warningWindow.entered',
        extra: {
          expiresAt: current.expiresAt,
          remainingSeconds: current.remainingSeconds,
          warningThresholdSeconds: warningThreshold,
        },
      });
    }

    if (
      previous.isExpiringSoon &&
      !current.isExpiringSoon &&
      current.remainingSeconds > 0
    ) {
      captureSessionMessage('Session left expiring-soon window', 'info', {
        event: 'session.expiration.warningWindow.exited',
        extra: {
          expiresAt: current.expiresAt,
          remainingSeconds: current.remainingSeconds,
        },
      });
    }

    previousExpirationStateRef.current = current;
  }, [expirationState, isLoaded, warningThreshold]);

  // Refresh session by making an API call
  // This triggers backend to update lastActivity and expiresAt
  const refreshSession = useCallback(async () => {
    console.log('🔄 useSessionExpiration: Refreshing session...');
    try {
      captureSessionMessage(
        'Refreshing session (backend lastActivity)',
        'info',
        {
          event: 'session.refresh.requested',
        },
      );

      await actions.fetchSessions();
      console.log('✅ useSessionExpiration: Session refreshed successfully');

      captureSessionMessage(
        'Refreshed session (backend lastActivity)',
        'info',
        {
          event: 'session.refresh.succeeded',
        },
      );
    } catch (error) {
      console.error(
        '❌ useSessionExpiration: Failed to refresh session:',
        error,
      );
      captureSessionException(error, {
        event: 'session.refresh.failed',
      });
      throw error;
    }
  }, [actions]);

  return {
    ...expirationState,
    refreshSession,
    fetchCurrentSession,
  };
}

export default useSessionExpiration;
